import { useEffect } from 'react';
import { useNodeStore } from '../stores/nodeStore';
import { useSelectionStore } from '../stores/selectionStore';
import { AppNode, NodeStatus } from '../types';

interface KeyboardShortcutsOptions {
  undo: () => void;
  saveHistory: () => void;
  clipboard: AppNode | null;
  setClipboard: (node: AppNode | null) => void;
  addNode: (node: AppNode) => void;
}

export const useKeyboardShortcuts = ({
  undo,
  saveHistory,
  clipboard,
  setClipboard,
  addNode,
}: KeyboardShortcutsOptions) => {
  const deleteNodes = useNodeStore((s) => s.deleteNodes);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        undo();
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'c') {
        const selectedArray = useSelectionStore.getState().getSelectedArray();
        const lastSelected = selectedArray[selectedArray.length - 1];
        if (lastSelected) {
          const nodeToCopy = useNodeStore.getState().nodeMap.get(lastSelected);
          if (nodeToCopy) {
            e.preventDefault();
            setClipboard(JSON.parse(JSON.stringify(nodeToCopy)));
          }
        }
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'v') {
        if (clipboard) {
          e.preventDefault();
          saveHistory();
          const newNode: AppNode = {
            ...clipboard,
            id: `n-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
            x: clipboard.x + 50,
            y: clipboard.y + 50,
            status: NodeStatus.IDLE,
            inputs: [],
          };
          addNode(newNode);
          useSelectionStore.getState().setSelectedNodeIds([newNode.id]);
        }
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        useSelectionStore.getState().selectAll();
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        const selectedArray = useSelectionStore.getState().getSelectedArray();
        if (selectedArray.length > 0) {
          deleteNodes(selectedArray);
        }
      }

      if (e.key === 'Escape') {
        useSelectionStore.getState().clearSelection();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, saveHistory, clipboard, setClipboard, addNode, deleteNodes]);
};
